/**
 * Omnisend.
 *
 * La cuenta es SIEMPRE la del cliente, nunca una nuestra: aquí se pega la clave
 * de SU cuenta y los contactos de sus formularios van a SUS listas. Por eso la
 * pantalla habla de «tu cuenta» todo el rato — quien llega aquí ya usa Omnisend
 * y lo que quiere saber es si esto va a mandar a ella o a otro sitio.
 *
 * Dos cosas que la pantalla tiene que dejar dichas, porque son las dos que
 * sorprenden después:
 *
 *   · La clave entra una vez y no vuelve a salir. Se guarda cifrada y de ella
 *     sólo se enseñan los últimos cuatro caracteres. Cambiarla es pegar otra.
 *   · Conectar la cuenta NO manda nada todavía. El envío se activa formulario a
 *     formulario, y suscribir a alguien pide además una casilla de opt-in.
 *     Sin decirlo aquí, la primera pregunta a soporte es «lo conecté y no llega
 *     nada», y la segunda —peor— «¿por qué no está suscrita esta gente?».
 */
import { useEffect, useState } from 'react'
import {
  IconAlertTriangle,
  IconCheck,
  IconExternalLink,
  IconForms,
  IconPlugConnected,
} from '@tabler/icons-react'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { OmnisendMark } from './OmnisendMark'
import { connectIntegration, disconnectIntegration, getIntegrations, type IntegrationState } from '../../../lib/api'
import { useI18n, translateServerError } from '../../../lib/i18n'

/** Dónde saca el cliente su clave, dicho tal y como se llama en Omnisend. */
const API_KEYS_URL = 'https://app.omnisend.com/integrations/api-keys'

export function OmnisendSettings({ projectId, token, projectRole = 'owner', onBack, onOpenForms }: {
  projectId: string
  token: string
  projectRole?: ProjectAccessRole
  onBack?: (() => void) | undefined
  /** A configurar el interruptor, que es el paso siguiente de verdad. */
  onOpenForms: () => void
}) {
  const { t } = useI18n()
  const [state, setState] = useState<IntegrationState | null>(null)
  const [loading, setLoading] = useState(true)
  const [apiKey, setApiKey] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  /* Quien puede pegar la clave puede pegar LA SUYA, y desde ese momento los
     contactos del negocio alimentan su lista. Por eso es de administración y no
     de quien edita el sitio. Se lee, se ve, no se toca. */
  const canManage = can(projectRole, 'integrations.manage')

  useEffect(() => {
    let alive = true
    setLoading(true)
    getIntegrations(projectId, token)
      .then((integrations) => { if (alive) setState(integrations.omnisend ?? null) })
      .catch(() => { if (alive) setState(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [projectId, token])

  async function handleConnect() {
    const key = apiKey.trim()
    if (!key || saving) return
    setSaving(true)
    setError(null)
    try {
      const result = await connectIntegration(projectId, 'omnisend', key, token)
      setState({
        connected: true,
        status: result.status,
        hint: result.hint,
        lastError: '',
        updatedAt: new Date().toISOString(),
      })
      /* La clave se borra del campo en cuanto se guarda: dejarla ahí a la vista
         no aporta nada —ya no se puede volver a leer del servidor— y sí deja una
         credencial escrita en una pantalla que alguien puede tener abierta. */
      setApiKey('')
    } catch (err) {
      setError(translateServerError(err, t('omnisend.connectFailed')))
    } finally {
      setSaving(false)
    }
  }

  async function handleDisconnect() {
    setSaving(true)
    setError(null)
    try {
      await disconnectIntegration(projectId, 'omnisend', token)
      setState(null)
      setConfirmingDisconnect(false)
    } catch (err) {
      setError(translateServerError(err, t('omnisend.disconnectFailed')))
    } finally {
      setSaving(false)
    }
  }

  const connected = state?.connected === true
  const invalid = connected && state?.status === 'invalid'

  return (
    <ProjectSettingsLayout
      title="Omnisend"
      description={t('omnisend.description')}
      onBack={onBack}
    >
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon dash-settings-brand-icon" aria-hidden="true">
            <OmnisendMark size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>
              {t(loading ? 'omnisend.checking' : invalid ? 'omnisend.keyBroken' : connected ? 'omnisend.connected' : 'omnisend.notConnected')}
            </h3>
            <p>
              {loading
                ? t('omnisend.loadingBody')
                : invalid
                  ? t('omnisend.invalidBody')
                  : connected
                    ? t('omnisend.connectedBody', { hint: state?.hint ?? '' })
                    : t('omnisend.emptyBody')}
            </p>
          </div>
          {connected && !loading ? (
            <span className={`dash-connect-status is-${invalid ? 'warn' : 'ok'}`}>
              {invalid
                ? <IconAlertTriangle size={13} aria-hidden="true" />
                : <IconCheck size={13} aria-hidden="true" />}
              {t(invalid ? 'omnisend.needsAttention' : 'omnisend.statusOk')}
            </span>
          ) : null}
        </div>

        {/* El motivo que dio Omnisend, cuando lo dio. Sin esto, una clave
            revocada y una borrada por error se ven exactamente igual. */}
        {invalid && state?.lastError ? (
          <div className="dash-inline-msg">
            <span>{state.lastError}</span>
          </div>
        ) : null}

        {canManage ? (
          <>
            <label className="dash-field">
              <span>{t(connected ? 'omnisend.changeKey' : 'omnisend.apiKey')}</span>
              <input
                type="password"
                value={apiKey}
                autoComplete="off"
                spellCheck={false}
                placeholder={t('omnisend.placeholder')}
                onChange={(event) => setApiKey(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') void handleConnect() }}
                disabled={saving}
              />
            </label>
            <p className="dash-card-caption">
              {t('omnisend.whereKeyPart1')}{' '}
              {/* Sin traducir a propósito: es cómo se llama esa pantalla DENTRO
                  de Omnisend, y allí está en inglés para todo el mundo.
                  Traducirla mandaría a buscar un menú que no existe. */}
              <strong>Store settings → API keys</strong>.{' '}
              <a href={API_KEYS_URL} target="_blank" rel="noopener noreferrer">
                {t('omnisend.openOmnisend')} <IconExternalLink size={12} aria-hidden="true" />
              </a>
              {' · '}
              {t('omnisend.stored')}
            </p>

            {error ? (
              <div className="dash-inline-msg">
                <span>{error}</span>
              </div>
            ) : null}

            <div className="dash-settings-card-foot">
              <button
                type="button"
                className="dash-primary-btn"
                onClick={() => void handleConnect()}
                disabled={saving || apiKey.trim().length === 0}
              >
                <IconPlugConnected size={14} aria-hidden="true" />
                {t(saving ? 'omnisend.checking' : connected ? 'omnisend.saveNewKey' : 'omnisend.connect')}
              </button>
              {connected ? (
                confirmingDisconnect ? (
                  <>
                    <button type="button" className="dash-danger-btn" onClick={() => void handleDisconnect()} disabled={saving}>
                      {t('omnisend.confirmDisconnect')}
                    </button>
                    <button type="button" className="dash-ghost-btn" onClick={() => setConfirmingDisconnect(false)} disabled={saving}>
                      {t('omnisend.cancel')}
                    </button>
                  </>
                ) : (
                  <button type="button" className="dash-secondary-btn" onClick={() => setConfirmingDisconnect(true)} disabled={saving}>
                    {t('omnisend.disconnect')}
                  </button>
                )
              ) : null}
            </div>
            {confirmingDisconnect ? (
              /* Lo que de verdad pasa al desconectar, dicho antes y no después:
                 lo ya enviado se queda en Omnisend —es de su cuenta— y los
                 interruptores de los formularios no se apagan, se quedan sin
                 efecto hasta que vuelva a haber cuenta. */
              <p className="dash-card-caption">
                {t('omnisend.disconnectNote')}
              </p>
            ) : null}
          </>
        ) : (
          <p className="dash-card-caption">
            {t('omnisend.adminOnly')}
          </p>
        )}
      </section>

      {/* Conectar no manda nada todavía. Es la confusión número uno de una
          integración así, y se evita diciéndolo en la propia pantalla. */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconForms size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('omnisend.stepsTitle')}</h3>
            <p>{t('omnisend.stepsLead')}</p>
          </div>
        </div>
        <ol className="dash-connect-steps">
          <li>
            <span className="dash-connect-step-num">1</span>
            {t('omnisend.step1')}
          </li>
          <li>
            <span className="dash-connect-step-num">2</span>
            {t('omnisend.step2Part1')} <strong>{t('omnisend.step2Strong')}</strong>{' '}
            {t('omnisend.step2Part2')}
          </li>
        </ol>
        <div className="dash-settings-card-foot">
          <button type="button" className="dash-secondary-btn" onClick={onOpenForms}>
            {t('omnisend.goToForms')}
          </button>
        </div>
      </section>
    </ProjectSettingsLayout>
  )
}
